"use client"

import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import AccessDenied from "@/components/custom-access-denied"
import { TRANSLATIONS } from "@/constants/admin-paths"
import { domainOptions } from "@/constants/domain-option"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { clearCachedTranslations, i18next, loadTranslations, t } from "@/languages/i18n"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import type { FormProps } from "antd"
import { Button, Form, Input, Select } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  checkKeyWebsiteExists,
  createTranslation,
  getTranslationById,
  updateTranslation,
} from "../services/translation"

interface TranslationFieldType {
  key: string
  vi: string
  en?: string
  cs?: string
  website: string
}

const invalidateTranslationCaches = async (website: string) => {
  clearCachedTranslations(website)

  // Strapi lifecycle đã DEL cache Tamda-api cho dictionary; ở đây chỉ cần
  // invalidate cache dịch tầng app (next-intl) qua route riêng.
  await fetch("/api/dictionaries/invalidate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      website,
      locale: i18next.language,
    }),
  }).catch(error => {
    console.warn("Failed to invalidate translation cache on server:", error)
  })

  await loadTranslations(i18next.language, website, { force: true }).catch(error => {
    console.warn("Failed to refresh translation cache on client:", error)
  })
}

const UpsertTranslation = () => {
  const [loading, setLoading] = useState(false)
  const [isFormReady, setIsFormReady] = useState(false)
  const [form] = Form.useForm<TranslationFieldType>()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id
  const { hasPermission: checkPermission } = useCheckPermission()
  const router = useRouter()

  const getNormalizedSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true)
    return normalizeFormData(values)
  }, [form])

  const {
    checkChange,
    commitSnapshot,
    message,
    handleCancel: guardCancel,
  } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })

  if (!checkPermission("translation", isEditMode ? "update" : "create")) {
    return <AccessDenied />
  }

  const onFinish: FormProps<TranslationFieldType>["onFinish"] = async values => {
    try {
      setLoading(true)

      const isDuplicate = await checkKeyWebsiteExists(values.key, values.website, isEditMode ? Number(id) : undefined)

      if (isDuplicate) {
        form.setFields([{ name: "key", errors: [t("Khóa này đã tồn tại trong website đã chọn")] }])
        return
      }

      const payload: Record<string, any> = {
        key: values.key,
        vi: values.vi,
        en: values.en || null,
        cs: values.cs || null,
        website: values.website,
      }

      if (isEditMode) {
        await updateTranslation(id!, payload)
        await invalidateTranslationCaches(values.website)
        commitSnapshot()
        message.success(t("Cập nhật bản dịch thành công"))
      } else {
        await createTranslation(payload)
        await invalidateTranslationCaches(values.website)
        commitSnapshot()
        message.success(t("Tạo bản dịch thành công"))
        router.push(createPath(TRANSLATIONS.LIST.PATH, searchParams))
      }
    } catch {
      message.error(t("Có lỗi xảy ra, vui lòng thử lại"))
    } finally {
      setLoading(false)
    }
  }

  const onFinishFailed: FormProps<TranslationFieldType>["onFinishFailed"] = () => { }

  useEffect(() => {
    if (!isEditMode || !id) {
      setIsFormReady(true)
      return
    }

    const fetchTranslation = async () => {
      try {
        setLoading(true)
        const response = await getTranslationById(Number(id))

        if (!response) {
          message.error(t("Không tìm thấy bản dịch"))
          return
        }

        const data = response.attributes || response
        form.setFieldsValue({
          key: data.key || "",
          vi: data.vi || "",
          en: data.en || "",
          cs: data.cs || "",
          website: data.website || undefined,
        })
        setIsFormReady(true)
      } catch {
        message.error(t("Lỗi khi tải dữ liệu bản dịch"))
      } finally {
        setLoading(false)
      }
    }

    fetchTranslation()
  }, [form, id, isEditMode, message])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot()
      })
      return () => cancelAnimationFrame(animationId)
    }
  }, [isFormReady, commitSnapshot])

  const initialValues = useMemo(() => ({}), [])

  const handleCancelClick = () => {
    const targetPath = createPath(TRANSLATIONS.LIST.PATH, searchParams)
    guardCancel(targetPath)
  }

  if (loading && isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={2} sidebarCards={0} />
  }

  const crumbs = [
    { title: t("Bản dịch"), href: createPath(TRANSLATIONS.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Thông tin")} collapsible={false}>
      <Form<TranslationFieldType>
        labelAlign="left"
        form={form}
        labelCol={{ span: 7 }}
        initialValues={initialValues}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        className="space-y-6"
        onValuesChange={checkChange}
      >
        <Form.Item
          name="key"
          label={<span className="text-base font-medium">{t("Khóa")}</span>}
          rules={[{ required: true, message: t("Vui lòng nhập khóa") }]}
        >
          <Input placeholder={t("Ví dụ: common.button.save")} />
        </Form.Item>

        <Form.Item
          name="website"
          label={<span className="text-base font-medium">{t("Website")}</span>}
          rules={[{ required: true, message: t("Vui lòng chọn website") }]}
        >
          <Select
            showSearch
            placeholder={t("Chọn website")}
            options={domainOptions}
            optionFilterProp="label"
            onChange={() => setTimeout(() => checkChange(), 0)}
          />
        </Form.Item>

        <Form.Item
          name="vi"
          label={<span className="text-base font-medium">{t("Nội dung tiếng Việt")}</span>}
          rules={[{ required: true, message: t("Vui lòng nhập nội dung tiếng Việt") }]}
        >
          <Input.TextArea rows={3} placeholder={t("Nội dung tiếng Việt")} />
        </Form.Item>

        <Form.Item name="en" label={<span className="text-base font-medium">{t("Nội dung tiếng Anh")}</span>}>
          <Input.TextArea rows={3} placeholder={t("Nội dung tiếng Anh")} />
        </Form.Item>

        <Form.Item name="cs" label={<span className="text-base font-medium">{t("Nội dung tiếng Séc")}</span>}>
          <Input.TextArea rows={3} placeholder={t("Nội dung tiếng Séc")} />
        </Form.Item>
      </Form>
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <>
          <Button onClick={handleCancelClick}>{t("Hủy")}</Button>
          <Button type="primary" onClick={() => form.submit()} loading={loading}>
            {isEditMode ? t("Cập nhật bản dịch") : t("Tạo bản dịch")}
          </Button>
        </>
      }
    />
  )

  return (
    <FormShell
      title={isEditMode ? t("Chỉnh sửa bản dịch") : t("Tạo bản dịch")}
      crumbs={crumbs}
      main={main}
      sidebar={null}
      actionBar={actionBar}
    />
  )
}

export default UpsertTranslation
